import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon,
  PlayArrow as ActiveIcon
} from '@mui/icons-material';
import { useTDD } from '../../context/TDDContext';

/**
 * TDD Progress Tracker Component
 * Shows current progress through the TDD workflow phases
 */
const TDDProgressTracker = ({ projectId }) => {
  const { tddState } = useTDD();

  // Define TDD workflow phases
  const tddPhases = [
    {
      label: 'Initialization',
      description: 'Setting up TDD workspace and extracting project files',
      key: 'initialization'
    },
    {
      label: 'Analysis',
      description: 'Analyzing user stories and breaking down into atomic tasks',
      key: 'analysis'
    },
    {
      label: 'Development',
      description: 'Executing TDD cycles - Red, Green, Refactor',
      key: 'development'
    },
    {
      label: 'Testing',
      description: 'Running tests and validating implementation',
      key: 'testing'
    },
    {
      label: 'Completion',
      description: 'Finalizing code and documentation',
      key: 'completion'
    }
  ];

  /**
   * Get current active step
   */
  const getCurrentStep = () => {
    const phaseIndex = tddPhases.findIndex(phase => phase.key === tddState.progress.phase);
    return phaseIndex >= 0 ? phaseIndex : 0;
  };

  /**
   * Get step status
   */
  const getStepStatus = (stepIndex) => {
    const currentStep = getCurrentStep();
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) {
      if (tddState.progress.phase === 'error') return 'error';
      return 'active';
    }
    return 'pending';
  };

  /**
   * Get step icon
   */
  const getStepIcon = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    switch (status) {
      case 'completed':
        return <CompleteIcon color="success" />;
      case 'active':
        return <ActiveIcon color="primary" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  /**
   * Format TDD cycle phase color
   */
  const getTDDCycleColor = (phase) => {
    switch (phase) {
      case 'red': return 'error';
      case 'green': return 'success';
      case 'refactor': return 'info';
      default: return 'default';
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          TDD Progress Tracker
        </Typography>

        {/* Overall Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tddState.progress.percentage}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={tddState.progress.percentage} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          {tddState.progress.message && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {tddState.progress.message}
            </Typography>
          )}
        </Box>

        {/* Current TDD Cycle Info */}
        {tddState.tddCycle.currentCycle > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity={getTDDCycleColor(tddState.tddCycle.cyclePhase)}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                <strong>Ralphio TDD Cycle {tddState.tddCycle.currentCycle}</strong>
                <br />
                Current Phase: <strong>{tddState.tddCycle.cyclePhase.toUpperCase()}</strong>
                {tddState.tddCycle.cyclePhase === 'red' && ' - Write failing test first'}
                {tddState.tddCycle.cyclePhase === 'green' && ' - Make test pass with minimal code'}
                {tddState.tddCycle.cyclePhase === 'refactor' && ' - Clean up and optimize code'}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Phase Stepper */}
        <Stepper activeStep={getCurrentStep()} orientation="vertical">
          {tddPhases.map((phase, index) => (
            <Step key={phase.key}>
              <StepLabel
                icon={getStepIcon(index)}
                error={getStepStatus(index) === 'error'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">
                    {phase.label}
                  </Typography>
                  <Chip 
                    label={getStepStatus(index)} 
                    size="small" 
                    color={
                      getStepStatus(index) === 'completed' ? 'success' :
                      getStepStatus(index) === 'active' ? 'primary' :
                      getStepStatus(index) === 'error' ? 'error' : 'default'
                    }
                  />
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  {phase.description}
                </Typography>
                
                {/* Show additional info for current step */}
                {getStepStatus(index) === 'active' && (
                  <Box sx={{ mt: 1 }}>
                    {tddState.currentTask.title && (
                      <Typography variant="body2">
                        Current Task: <strong>{tddState.currentTask.title}</strong>
                      </Typography>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* TDD Cycle History */}
        {tddState.tddCycle.cycleHistory.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Recent TDD Cycles:
            </Typography>
            <List dense>
              {tddState.tddCycle.cycleHistory.slice(-3).map((cycle, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {cycle.result === 'success' ? (
                      <CompleteIcon color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        Task completed {cycle.result === 'success' ? 'successfully' : 'with errors'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(cycle.timestamp).toLocaleTimeString()}
                        {cycle.error && ` - ${cycle.error}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Workspace Stats */}
        {tddState.workspace.status === 'ready' && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Workspace Status:</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`${tddState.workspace.files?.length || 0} files`}
                size="small" 
                color="info" 
              />
              {tddState.testResults.passed > 0 && (
                <Chip 
                  label={`${tddState.testResults.passed} tests passed`}
                  size="small" 
                  color="success" 
                />
              )}
              {tddState.testResults.failed > 0 && (
                <Chip 
                  label={`${tddState.testResults.failed} tests failed`}
                  size="small" 
                  color="error" 
                />
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TDDProgressTracker;